import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getNightlyRates, getMinimumStays, getPricingSettings, getPromoCodes } from "@/services/pricingService";
import { createReservation } from "@/services/reservationService";
import { validateMinimumStay, calculateBookingDetails } from "@/utils/bookingCalculations";
import type { PromoCode } from "@/types/supabase";
import type { BookingFormData } from "@/components/booking/BookingForm";

export function useBooking(initialCheckIn?: Date, initialCheckOut?: Date) {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState<Date | undefined>(initialCheckIn);
  const [checkOut, setCheckOut] = useState<Date | undefined>(initialCheckOut);
  const [guestsCount, setGuestsCount] = useState<number>(2);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState<PromoCode | null>(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  // Fetch data using React Query
  const { data: nightlyRates } = useQuery({
    queryKey: ['nightly-rates'],
    queryFn: getNightlyRates
  });
  
  const { data: minimumStays } = useQuery({
    queryKey: ['minimum-stays'],
    queryFn: getMinimumStays
  });
  
  const { data: pricingSettings } = useQuery({
    queryKey: ['pricing-settings'],
    queryFn: getPricingSettings
  });

  const { data: promoCodes } = useQuery({
    queryKey: ['promo-codes'],
    queryFn: getPromoCodes
  });

  // Auto-apply eligible promo codes
  useEffect(() => {
    if (!checkIn || !checkOut || !promoCodes) return;

    const now = new Date();
    const autoPromo = promoCodes.find(promo => {
      const startDate = new Date(promo.start_date);
      const expirationDate = promo.expiration_date ? new Date(promo.expiration_date) : null;
      const checkInDate = new Date(checkIn);
      
      // Check if promo is auto-apply and active
      if (!promo.auto_apply) return false;
      if (startDate > now) return false;
      if (expirationDate && expirationDate < now) return false;
      if (promo.max_uses && promo.times_used >= promo.max_uses) return false;

      // Check applicable check-in date range
      if (promo.applicable_check_in_start) {
        const applicableStart = new Date(promo.applicable_check_in_start);
        if (checkInDate < applicableStart) return false;
      }
      if (promo.applicable_check_in_end) {
        const applicableEnd = new Date(promo.applicable_check_in_end);
        if (checkInDate > applicableEnd) return false;
      }

      return true;
    });

    if (autoPromo && (!appliedPromoCode || !autoPromo.auto_apply)) {
      setAppliedPromoCode(autoPromo);
    }
  }, [checkIn, checkOut, promoCodes, appliedPromoCode]);

  // Handle date selection
  const handleDatesSelected = (dates: { checkIn: Date | undefined; checkOut: Date | undefined }) => {
    setCheckIn(dates.checkIn);
    setCheckOut(dates.checkOut);
    
    if (dates.checkIn && dates.checkOut) {
      const validation = validateMinimumStay(dates.checkIn, dates.checkOut, minimumStays);
      if (!validation.isValid) {
        toast.error(`Pour cette période, la durée minimum de séjour est de ${validation.minimumNights} nuits.`);
      }
    }
  };

  // Send confirmation email
  const sendConfirmationEmail = async (reservation: any) => {
    try {
      console.log("Envoi de l'email de confirmation pour la réservation:", reservation.id);
      
      const response = await fetch('https://kxrduwrurqjvouiqobyc.supabase.co/functions/v1/send-reservation-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt4cmR1d3J1cnFqdm91aXFvYnljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUwMTEzOTcsImV4cCI6MjA2MDU4NzM5N30.p8YjQNTTWTZ91lzhLgyUfFKwFnY8LrSyuiat9vh2zYY`
        },
        body: JSON.stringify({ reservation })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Erreur lors de l\'envoi de l\'email:', errorData);
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Email de confirmation envoyé avec succès:', result);
      
      return result;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de confirmation:', error);
      // Ne pas bloquer le processus de réservation si l'email échoue
      toast.error("Réservation créée mais l'email de confirmation n'a pas pu être envoyé.");
    }
  };

  // Handle form submission
  const handleSubmit = async (formData: BookingFormData) => {
    if (!checkIn || !checkOut) {
      toast.error("Veuillez sélectionner vos dates de séjour");
      return;
    }
    
    if (!formData.agreeTerms) {
      toast.error("Veuillez accepter les conditions générales");
      return;
    }
    
    const validation = validateMinimumStay(checkIn, checkOut, minimumStays);
    if (!validation.isValid) {
      toast.error(`Pour cette période, la durée minimum de séjour est de ${validation.minimumNights} nuits.`);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const bookingDetails = calculateBookingDetails(
        checkIn,
        checkOut,
        guestsCount,
        nightlyRates,
        pricingSettings,
        appliedPromoCode
      );
      
      if (!bookingDetails) {
        toast.error("Impossible de calculer les détails de la réservation");
        return;
      }
      
      console.log("Création de la réservation avec les données:", {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        guests: guestsCount,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        special_requests: formData.specialRequests || null,
        total_price: bookingDetails.total,
        status: 'pending' as const
      });
      
      const reservation = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        guests: guestsCount,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        special_requests: formData.specialRequests || null,
        total_price: bookingDetails.total,
        status: 'pending' as const,
        source: 'Direct' as const
      };
      
      const savedReservation = await createReservation(reservation);
      console.log("Réservation créée avec succès:", savedReservation);
      
      // Envoyer l'email de confirmation
      await sendConfirmationEmail(savedReservation);
      
      toast.success("Votre réservation a été prise en compte ! Vous allez recevoir un email de confirmation.");
      
      navigate('/reservation-success', { state: { reservation: savedReservation } });
      
    } catch (error: any) {
      console.error("Erreur lors de la création de la réservation:", error);
      toast.error("Une erreur est survenue lors de la réservation. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle promo code application
  const handleApplyPromoCode = async (code: string) => {
    if (!code.trim()) {
      toast.error("Veuillez entrer un code promo");
      return;
    }

    setIsValidatingPromo(true);
    
    try {
      if (!promoCodes) {
        console.error("Impossible de vérifier les codes promo: promoCodes est null");
        toast.error("Impossible de vérifier les codes promo");
        setIsValidatingPromo(false);
        return;
      }

      console.log("Codes promo disponibles:", promoCodes);

      const currentDate = new Date();
      const foundCode = promoCodes.find(
        promoCode => 
          promoCode.code === code && 
          new Date(promoCode.start_date) <= currentDate &&
          (!promoCode.expiration_date || new Date(promoCode.expiration_date) >= currentDate) &&
          (!promoCode.max_uses || promoCode.times_used < promoCode.max_uses)
      );

      console.log("Code trouvé:", foundCode);

      if (foundCode) {
        const typedPromoCode: PromoCode = {
          ...foundCode,
          discount_type: foundCode.discount_type as "percentage" | "fixed"
        };
        
        setAppliedPromoCode(typedPromoCode);
        toast.success("Code promo appliqué avec succès");
      } else {
        toast.error("Code promo invalide ou expiré");
      }
    } catch (error) {
      console.error("Erreur lors de la validation du code promo:", error);
      toast.error("Erreur lors de la validation du code promo");
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleGuestsCountChange = (count: number) => {
    setGuestsCount(count);
  };

  // Calculate booking details
  const bookingDetails = checkIn && checkOut ? calculateBookingDetails(
    checkIn,
    checkOut,
    guestsCount,
    nightlyRates,
    pricingSettings,
    appliedPromoCode
  ) : null;

  return {
    checkIn,
    checkOut,
    guestsCount,
    isSubmitting,
    showWaitlistDialog,
    showContactPopup,
    setShowContactPopup,
    setShowWaitlistDialog,
    appliedPromoCode,
    isValidatingPromo,
    bookingDetails,
    handleDatesSelected,
    handleSubmit,
    handleApplyPromoCode,
    handleGuestsCountChange
  };
}
